import React from 'react';
import { TrendingUp, TrendingDown, Minus, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import type { MonthlyRevenueRecord } from '../../../hooks/useMonthlyRevenue';
import { fmtUSD, fmtPct, fmtMonth } from '../../../utils/monthlyRevenueUtils';

interface KpiCardsProps {
  latestRecord: MonthlyRevenueRecord;
  prevRecord: MonthlyRevenueRecord | null;
  currentYearAvg: number;
  prevYearAvg: number;
  yoyAvgChange: number;
}

const DeltaBadge: React.FC<{ pct: number; label: string }> = ({ pct, label }) => {
  const up = pct >= 0;
  return (
    <div className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${up ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
      {up ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
      {fmtPct(pct)} {label}
    </div>
  );
};

export const KpiCards: React.FC<KpiCardsProps> = ({
  latestRecord,
  prevRecord,
  currentYearAvg,
  prevYearAvg,
  yoyAvgChange,
}) => {
  const momPct = latestRecord.mom_change_percent;
  const yoyPct = latestRecord.yoy_change_percent;
  const momUp = momPct >= 0;
  const yoyUp = yoyPct >= 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* MoM Comparison Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-0.5 h-4 rounded-full bg-blue-500 flex-none" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
            Month-over-Month
          </span>
        </div>

        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <p className="text-[11px] text-slate-500 mb-1">{fmtMonth(latestRecord.invoice_month)}</p>
            <p className="text-2xl font-bold text-slate-900 tabular-nums leading-tight">
              {fmtUSD(latestRecord.global_invoiced_usd)}
            </p>
          </div>
          <div className={`flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 rounded-lg ${momUp ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {momUp ? <TrendingUp size={15} /> : <TrendingDown size={15} />}
            {fmtPct(momPct)}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] text-slate-500 font-medium mb-1">
              {prevRecord ? fmtMonth(prevRecord.invoice_month) : 'Prior Month'}
            </p>
            <p className="text-sm font-bold text-slate-700 tabular-nums">
              {fmtUSD(latestRecord.prev_month_invoiced)}
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] text-slate-500 font-medium mb-1">MoM Change</p>
            <p className={`text-sm font-bold tabular-nums ${momUp ? 'text-emerald-700' : 'text-red-700'}`}>
              {momUp ? '+' : ''}{fmtUSD(latestRecord.mom_change_usd)}
            </p>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
            latestRecord.trend_position === 'ABOVE_TREND' ? 'bg-emerald-50 text-emerald-700' :
            latestRecord.trend_position === 'BELOW_TREND' ? 'bg-red-50 text-red-700' :
            'bg-blue-50 text-blue-700'
          }`}>
            {latestRecord.trend_position.replace(/_/g, ' ')}
          </span>
          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-50 text-amber-700">
            {latestRecord.volatility_level.replace(/_/g, ' ')}
          </span>
        </div>
      </div>

      {/* YoY Comparison Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-0.5 h-4 rounded-full bg-violet-500 flex-none" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
            Year-over-Year
          </span>
        </div>

        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <p className="text-[11px] text-slate-500 mb-1">YoY change ({fmtMonth(latestRecord.invoice_month)})</p>
            <p className={`text-2xl font-bold tabular-nums leading-tight ${yoyUp ? 'text-emerald-700' : 'text-red-700'}`}>
              {fmtPct(yoyPct)}
            </p>
          </div>
          <div className={`flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 rounded-lg ${yoyUp ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {yoyUp ? <ArrowUpRight size={15} /> : <ArrowDownRight size={15} />}
            {fmtUSD(Math.abs(latestRecord.yoy_change_usd))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] text-slate-500 font-medium mb-1">Current Year Avg</p>
            <p className="text-sm font-bold text-slate-700 tabular-nums">{fmtUSD(currentYearAvg)}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] text-slate-500 font-medium mb-1">Prior Year Avg</p>
            <p className="text-sm font-bold text-slate-700 tabular-nums">{fmtUSD(prevYearAvg)}</p>
          </div>
        </div>

        <div className="mt-3">
          <DeltaBadge pct={yoyAvgChange} label="avg YoY" />
        </div>
      </div>
    </div>
  );
};

export default KpiCards;
